/**
 * Secure Storage Utilities
 *
 * This module provides secure data storage patterns for handling sensitive information
 * in compliance with GDPR Article 32 and DPA 2018 requirements.
 *
 * Key Features:
 * - Automatic data expiration
 * - Session-based storage (cleared on browser close)
 * - Sensitive data cleanup
 * - Activity monitoring and timeout
 */

// List of sensitive data keys that should never be stored in localStorage
const SENSITIVE_KEYS = [
  'disclosure_draft',
  'disclosure_data',
  'resume-draft',
  'interview-pitch',
] as const;

// Session timeout in milliseconds (30 minutes of inactivity)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Last activity timestamp key
const LAST_ACTIVITY_KEY = 'last_activity';

/**
 * Interface for data stored with expiration
 */
interface StoredDataWithExpiry<T> {
  value: T;
  expiry: number;
}

/**
 * Clears all sensitive data from both localStorage and sessionStorage
 *
 * This should be called:
 * - On explicit logout
 * - After session timeout (30 minutes inactivity)
 * - When the user navigates away from sensitive pages
 */
export function clearSensitiveData(): void {
  try {
    // Clear sensitive keys from localStorage
    SENSITIVE_KEYS.forEach(key => {
      localStorage.removeItem(key);
    });

    // Clear interview answers (dynamic keys)
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach(key => {
      if (key.startsWith('interview-q-')) {
        localStorage.removeItem(key);
      }
    });

    // Clear all sessionStorage (it's session-scoped anyway)
    sessionStorage.clear();

    console.info('[Security] Sensitive data cleared successfully');
  } catch (error) {
    console.error('[Security] Error clearing sensitive data:', error);
  }
}

/**
 * Saves data to sessionStorage with automatic expiration
 *
 * Use this for temporary, non-sensitive data like form drafts
 * that should be cleared when the browser closes.
 *
 * @param key - Storage key
 * @param value - Data to store (must be JSON-serializable)
 * @param ttlMinutes - Time to live in minutes (default: 60 minutes)
 */
export function saveWithExpiry<T>(key: string, value: T, ttlMinutes: number = 60): void {
  try {
    const now = new Date();
    const item: StoredDataWithExpiry<T> = {
      value,
      expiry: now.getTime() + ttlMinutes * 60 * 1000,
    };
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.error('[Security] Storage quota exceeded. Clearing expired data...');
      clearExpiredData();
      // Retry after clearing
      try {
        const now = new Date();
        const item: StoredDataWithExpiry<T> = {
          value,
          expiry: now.getTime() + ttlMinutes * 60 * 1000,
        };
        sessionStorage.setItem(key, JSON.stringify(item));
      } catch (retryError) {
        console.error('[Security] Failed to save data after clearing expired items:', retryError);
      }
    } else {
      console.error('[Security] Error saving data:', error);
    }
  }
}

/**
 * Retrieves data from sessionStorage and validates expiration
 *
 * @param key - Storage key
 * @returns The stored value or null if expired/not found
 */
export function getWithExpiry<T>(key: string): T | null {
  try {
    const itemStr = sessionStorage.getItem(key);
    if (!itemStr) {
      return null;
    }

    const item: StoredDataWithExpiry<T> = JSON.parse(itemStr);
    const now = new Date();

    // Check if expired
    if (now.getTime() > item.expiry) {
      sessionStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch (error) {
    console.error('[Security] Error retrieving data:', error);
    return null;
  }
}

/**
 * Clears all expired data from sessionStorage
 */
export function clearExpiredData(): void {
  try {
    const now = new Date();
    const keys = Object.keys(sessionStorage);

    keys.forEach(key => {
      try {
        const itemStr = sessionStorage.getItem(key);
        if (itemStr) {
          const item = JSON.parse(itemStr);
          if (item.expiry && now.getTime() > item.expiry) {
            sessionStorage.removeItem(key);
          }
        }
      } catch {
        // Skip items that aren't in our format
      }
    });
  } catch (error) {
    console.error('[Security] Error clearing expired data:', error);
  }
}

/**
 * Updates the last activity timestamp
 * Call this on user interactions to prevent premature timeout
 */
export function updateActivity(): void {
  try {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  } catch (error) {
    console.error('[Security] Error updating activity timestamp:', error);
  }
}

/**
 * Checks if the session has timed out due to inactivity
 * @returns true if the session has timed out
 */
export function isSessionTimedOut(): boolean {
  try {
    const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) {
      // No activity recorded yet, initialize it
      updateActivity();
      return false;
    }

    const now = Date.now();
    const lastActivityTime = parseInt(lastActivity, 10);

    return (now - lastActivityTime) > SESSION_TIMEOUT;
  } catch (error) {
    console.error('[Security] Error checking session timeout:', error);
    return false;
  }
}

/**
 * Initializes session timeout monitoring
 *
 * This should be called once when the app loads.
 * It will automatically clear sensitive data after 30 minutes of inactivity.
 */
export function initSessionTimeout(): void {
  // Check for timeout on page load
  if (isSessionTimedOut()) {
    console.warn('[Security] Session timed out. Clearing sensitive data...');
    clearSensitiveData();
  }

  // Update activity on user interactions
  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  activityEvents.forEach(event => {
    document.addEventListener(event, updateActivity, { passive: true });
  });

  // Check for timeout every minute
  setInterval(() => {
    if (isSessionTimedOut()) {
      console.warn('[Security] Session timed out. Clearing sensitive data...');
      clearSensitiveData();

      // Optionally, redirect to login or show a timeout message
      // window.location.href = '/auth';
    }
  }, 60 * 1000); // Check every minute

  // Clear sensitive data on page unload
  window.addEventListener('beforeunload', () => {
    // Note: We don't clear sessionStorage here as it should persist across tab refreshes
    // but it will be cleared when the browser tab/window is closed
  });

  // Initialize activity timestamp
  updateActivity();
}

/**
 * Migrates data from localStorage to sessionStorage
 * Use this to transition existing stored data to the more secure sessionStorage
 *
 * @param key - The key to migrate
 */
export function migrateToSessionStorage(key: string): void {
  try {
    const data = localStorage.getItem(key);
    if (data) {
      sessionStorage.setItem(key, data);
      localStorage.removeItem(key);
      console.info(`[Security] Migrated ${key} from localStorage to sessionStorage`);
    }
  } catch (error) {
    console.error('[Security] Error migrating data:', error);
  }
}

/**
 * Session Encryption Key Management
 *
 * This uses a per-session encryption key that is:
 * - Generated once per browser session
 * - Stored in memory only (never persisted)
 * - Cleared when the browser is closed
 * - Different for each session
 *
 * This provides defense-in-depth against:
 * - XSS attacks reading sessionStorage
 * - Malicious browser extensions
 * - Physical access during active session
 */

// Session encryption key - stored in memory only, never persisted
let sessionEncryptionKey: string | null = null;

/**
 * Generates or retrieves the session encryption key
 * The key is generated once per page load and stored in memory
 */
function getSessionKey(): string {
  if (!sessionEncryptionKey) {
    // Generate a random key for this session
    // Using crypto.getRandomValues for cryptographic randomness
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    sessionEncryptionKey = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    console.info('[Security] Session encryption key generated (memory only)');
  }
  return sessionEncryptionKey;
}

/**
 * Saves encrypted data to sessionStorage with automatic expiration
 *
 * Use this for sensitive data like:
 * - Criminal record disclosures
 * - Personal information (PII)
 * - Interview answers
 * - Any data that should not be readable if device is compromised
 *
 * @param key - Storage key
 * @param value - Data to encrypt and store (must be JSON-serializable)
 * @param ttlMinutes - Time to live in minutes (default: 60 minutes)
 */
export async function saveSecure<T>(key: string, value: T, ttlMinutes: number = 60): Promise<void> {
  try {
    const now = new Date();
    const item: StoredDataWithExpiry<T> = {
      value,
      expiry: now.getTime() + ttlMinutes * 60 * 1000,
    };

    // Serialize to JSON first
    const jsonString = JSON.stringify(item);

    // Encrypt using session key
    const encryptedData = await encryptData(jsonString, getSessionKey());

    // Store encrypted data
    sessionStorage.setItem(key, encryptedData);
    console.info(`[Security] Encrypted data saved for key: ${key}`);
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.error('[Security] Storage quota exceeded. Clearing expired data...');
      clearExpiredData();
      // Retry after clearing
      try {
        const now = new Date();
        const item: StoredDataWithExpiry<T> = {
          value,
          expiry: now.getTime() + ttlMinutes * 60 * 1000,
        };
        const jsonString = JSON.stringify(item);
        const encryptedData = await encryptData(jsonString, getSessionKey());
        sessionStorage.setItem(key, encryptedData);
      } catch (retryError) {
        console.error('[Security] Failed to save encrypted data after clearing:', retryError);
      }
    } else {
      console.error('[Security] Error saving encrypted data:', error);
    }
  }
}

/**
 * Retrieves and decrypts data from sessionStorage
 *
 * @param key - Storage key
 * @returns The decrypted value or null if expired/not found/decryption failed
 */
export async function getSecure<T>(key: string): Promise<T | null> {
  try {
    const encryptedData = sessionStorage.getItem(key);
    if (!encryptedData) {
      return null;
    }

    // Decrypt using session key
    const decryptedString = await decryptData(encryptedData, getSessionKey());
    const item: StoredDataWithExpiry<T> = JSON.parse(decryptedString);
    const now = new Date();

    // Check if expired
    if (now.getTime() > item.expiry) {
      sessionStorage.removeItem(key);
      console.info(`[Security] Expired encrypted data removed for key: ${key}`);
      return null;
    }

    return item.value;
  } catch (error) {
    console.error('[Security] Error retrieving encrypted data:', error);
    // If decryption fails, remove the corrupted data
    sessionStorage.removeItem(key);
    return null;
  }
}

/**
 * Clears the session encryption key from memory
 * Call this on logout or when you want to invalidate all encrypted session data
 */
export function clearSessionKey(): void {
  sessionEncryptionKey = null;
  console.info('[Security] Session encryption key cleared from memory');
}

/**
 * WARNING: This function uses the Web Crypto API for client-side encryption.
 *
 * IMPORTANT SECURITY NOTES:
 * - Client-side encryption provides limited security benefits
 * - The encryption key must be stored somewhere accessible to the client
 * - This is NOT a substitute for server-side encryption and HTTPS
 * - For truly sensitive data (PII, criminal records), use server-side storage only
 *
 * This function is provided for educational purposes and for encrypting
 * non-critical data that might benefit from an additional layer of obfuscation.
 *
 * DO NOT USE THIS for criminal records or highly sensitive PII.
 */

/**
 * Encrypts data using Web Crypto API (AES-GCM)
 *
 * WARNING: See security notes above before using.
 *
 * @param data - Data to encrypt
 * @param password - Password for encryption (user should provide this)
 * @returns Encrypted data as base64 string
 */
export async function encryptData(data: string, password: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);

    // Derive key from password
    const passwordBuffer = encoder.encode(password);
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    const salt = crypto.getRandomValues(new Uint8Array(16));
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      dataBuffer
    );

    // Combine salt + iv + encrypted data
    const combined = new Uint8Array(salt.length + iv.length + encryptedBuffer.byteLength);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encryptedBuffer), salt.length + iv.length);

    // Convert to base64
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('[Security] Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypts data encrypted with encryptData()
 *
 * @param encryptedData - Base64 encrypted data
 * @param password - Password used for encryption
 * @returns Decrypted data as string
 */
export async function decryptData(encryptedData: string, password: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();

    // Decode base64
    const combined = new Uint8Array(
      atob(encryptedData)
        .split('')
        .map(char => char.charCodeAt(0))
    );

    // Extract salt, iv, and encrypted data
    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const encryptedBuffer = combined.slice(28);

    // Derive key from password
    const passwordBuffer = encoder.encode(password);
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedBuffer
    );

    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error('[Security] Decryption error:', error);
    throw new Error('Failed to decrypt data');
  }
}
